<script setup lang="ts">
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@layouts/utils'
import { useStorage } from '@vueuse/core'
import { $fetch } from 'ofetch'
import { cookieRef, namespaceConfig } from '@layouts/stores/config'
import { useBrandingStore } from '@/stores/branding'
import { useDocumentBranding } from '@/composables/useDocumentBranding'

const vuetifyTheme = useTheme()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const brandingStore = useBrandingStore()

useDocumentBranding()

const setPrimaryColor = useDebounceFn((color: { main: string; darken: string }) => {
  vuetifyTheme.themes.value[vuetifyTheme.name.value].colors.primary = color.main
  vuetifyTheme.themes.value[vuetifyTheme.name.value].colors['primary-darken-1'] = color.darken

  // ℹ️ We need to store this color value in cookie so vuetify plugin can pick on next reload
  cookieRef<string | null>(`${vuetifyTheme.name.value}ThemePrimaryColor`, null).value = color.main
  cookieRef<string | null>(`${vuetifyTheme.name.value}ThemePrimaryDarkenColor`, null).value = color.darken

  // ℹ️ Update initial loader color
  useStorage<string | null>(namespaceConfig('initial-loader-color'), null).value = color.main
}, 100)

const color = ref<string>(vuetifyTheme.current.value.colors.primary)
onMounted(async () => {
  try {
    const data = await $fetch('/api/organizations/get-meta')
    brandingStore.applyMetaBranding({
      logo: data?.logo,
      title: data?.title,
      browserTitle: data?.browserTitle,
      favicon: data?.favicon,
    })

    if (data?.themeColor && data.themeColorDarken) {
      color.value = data.themeColor
      setPrimaryColor({ main: data.themeColor, darken: data.themeColorDarken })
    }
  }
  catch {
  }
})

</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(color)}`">
      <RouterView />
      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>
